import {onMounted, onUnmounted, readonly, ref} from 'vue'
import {
    CheckConsentStatusCommand,
    Consent,
    ConsentStatus,
    InitiateConsentCommand,
    RefreshConsentAccountsCommand,
    RefreshConsentAccountsResponse,
    RevokeConsentCommand,
    RevokeConsentResponse
} from '@/bank/consent/consent.types'
import {get, getCatching, postCatching} from '@/bank/bankApi.service'
import {notifyError} from '@/services/infrastructure/notification.service'
import emitter, {useSingletonEmitter} from '@/services/infrastructure/emitter'
import {AUTH_STATE_CHANGE, LOGOUT} from '@/types/events'
import {Account} from '@/bank/account/account.types'
import {CONSENT_CREATED, CONSENT_UPDATED} from '@/bank/consent/consent.events'

const isInitialized = ref<boolean>(false)
const consents = ref<Consent[]>([])

export const useConsent = () => {
    const {onEvents, cleanUp} = useSingletonEmitter('banking:consent-service')

    const getAccountsByConsent = async (uuid: string): Promise<Account[]> => await get(`/users/self/consents/${uuid}/accounts`)

    const fetchConsents = async () => {
        const result = await getCatching<Consent[]>('/users/self/consents')
        if (result.isLeft()) {
            consents.value = []
            notifyError({title: 'Kunne ikke hente samtykker', description: {descriptionMessage: result.error.detail, error: result.error}})
        } else {
            consents.value = result.value
        }
    }

    const initiateConsent = async (orgNo: string, bic: string, bankUserId: string) => {
        const result = await postCatching<Consent>('/consents/:initiate', {orgNo: orgNo, bic: bic, bankUserId: bankUserId, redirectUrl: window.location.href.split('?')[0]} as InitiateConsentCommand)
        if (result.isLeft()) {
            notifyError({title: 'Kunne ikke initiere nytt samtykke', description: {descriptionMessage: result.error.detail, error: result.error}})
        } else {
            emitter.emit(CONSENT_CREATED)
        }
    }

    const checkConsentStatus = async (uuid: string) => {
        const result = await postCatching<Consent>('/consents/-/:check-status', {consentUuid: uuid} as CheckConsentStatusCommand)
        if (result.isLeft()) {
            notifyError({title: 'Kunne ikke sjekke status på samtykke', description: {descriptionMessage: result.error.detail, error: result.error}})
        } else {
            if (result.value.status === ConsentStatus.active) await refreshConsentAccounts(result.value)
            emitter.emit(CONSENT_UPDATED)
        }
    }

    const revokeConsent = async (uuid: string) => {
        const result = await postCatching<RevokeConsentResponse>('/consents/-/:revoke', {consentUuid: uuid} as RevokeConsentCommand)
        if (result.isLeft()) {
            notifyError({title: 'Kunne ikke trekke tilbake samtykke', description: {descriptionMessage: result.error.detail, error: result.error}})
        } else {
            emitter.emit(CONSENT_UPDATED)
        }
    }

    const refreshConsentAccounts = async (consent: Consent) => {
        const result = await postCatching<RefreshConsentAccountsResponse>('/consents/-/:refresh-accounts', {consentUuid: consent.uuid} as RefreshConsentAccountsCommand)
        if (result.isLeft()) {
            notifyError({title: 'Kunne ikke oppdatere kontoer for samtykke', description: {descriptionMessage: result.error.detail, error: result.error}})
        }
    }

    if (!isInitialized.value) {
        isInitialized.value = true
        fetchConsents()
    }

    onMounted(async () => {
        onEvents([CONSENT_CREATED, CONSENT_UPDATED], fetchConsents)
        onEvents([LOGOUT, AUTH_STATE_CHANGE], () => {
            consents.value = []
            isInitialized.value = false
        })
    })

    onUnmounted(() => {
        cleanUp()
    })

    return {
        consents: readonly(consents),
        fetchConsents,
        initiateConsent,
        checkConsentStatus,
        getAccountsByConsent,
        revokeConsent
    }
}
